'use client'
import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ArrowDownIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import Button from '@/components/Button'

const Hero = () => {
  const containerRef = useRef(null)
  const barsRef = useRef(null)
  
  // Text & Element Refs for GSAP
  const line1Ref = useRef(null)
  const line1EmphRef = useRef(null)
  const line2Ref = useRef(null)
  const line2EmphRef = useRef(null)
  const paraRef = useRef(null)
  const ctaRef = useRef(null)
  const dividerRef = useRef(null)
  const scrollIndicatorRef = useRef(null)

  useEffect(() => {
    // GSAP Context for easy cleanup
    const ctx = gsap.context(() => {
        // 1. Initial Setups (Invisible start states)
        gsap.set([line1Ref.current, line2Ref.current], { x: -50, autoAlpha: 0 })
        gsap.set([line1EmphRef.current, line2EmphRef.current], { x: 50, autoAlpha: 0 })
        gsap.set([paraRef.current, ctaRef.current, dividerRef.current], { y: 30, autoAlpha: 0 })
        gsap.set(scrollIndicatorRef.current, { autoAlpha: 0, y: -20 })

        // 2. Bar Animation (The "Empire Pillars" rising)
        if (barsRef.current) {
            const bars = barsRef.current.querySelectorAll('span')
            gsap.fromTo(bars, 
                { scaleY: 0, transformOrigin: 'bottom' },
                {
                    scaleY: 1,
                    duration: 1.5,
                    ease: 'power4.out',
                    stagger: {
                        amount: 0.8,
                        from: "center" // Ripple effect from center out
                    }
                }
            )
        }

        // 3. Main Text Timeline
        const tl = gsap.timeline({ delay: 0.5 })

        tl.to([line1Ref.current, line1EmphRef.current], {
            x: 0,
            autoAlpha: 1,
            duration: 1,
            ease: 'power3.out'
        })
        .to([line2Ref.current, line2EmphRef.current], {
            x: 0,
            autoAlpha: 1,
            duration: 1,
            ease: 'power3.out'
        }, "-=0.8") // Slight overlap for fluidity
        .to(paraRef.current, {
            y: 0,
            autoAlpha: 1,
            duration: 0.8,
            ease: 'power2.out'
        }, "-=0.6")
        .to(dividerRef.current, {
            y: 0,
            autoAlpha: 1,
            duration: 0.8,
            ease: 'power2.out'
        }, "-=0.6")
        .to(ctaRef.current, {
            y: 0,
            autoAlpha: 1,
            duration: 0.8,
            ease: 'back.out(1.7)' // Subtle bounce
        }, "-=0.4")
        .to(scrollIndicatorRef.current, {
            y: 0,
            autoAlpha: 1,
            duration: 0.5
        })

    }, containerRef)

    return () => ctx.revert()
  }, [])

  return (
    <div ref={containerRef} className='hero-container relative w-full bg-primary overflow-hidden flex items-start justify-center pt-[var(--header-height,6rem)] min-[1070px]:pt-[18vh] pb-20 min-[1070px]:pb-0'>
        
        {/* --- BACKGROUND LAYER --- */}
        {/* Radial Spotlight: Creates depth behind the text so it pops against the black */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-900/80 via-black to-black z-0 pointer-events-none"></div>
        
        {/* Optional: Subtle texture overlay */}
        <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] z-0 pointer-events-none mix-blend-overlay"></div>

        {/* --- ANIMATED EMPIRE PILLARS --- */}
        {/* We programmatically generate these for cleaner code */}
        <div ref={barsRef} className='absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 sm:px-10 h-[60%] z-0 opacity-20 pointer-events-none gap-2 sm:gap-4'>
            {[0.6, 0.7, 0.5, 0.8, 0.9, 1.0, 0.9, 0.8, 0.5, 0.7, 0.6].map((h, i) => (
                <span 
                    key={i} 
                    className={`w-full rounded-t-sm border-t border-secondary/30 bg-gradient-to-b from-gray-500 via-black to-black`}
                    style={{ height: `${h * 100}%` }}
                />
            ))}
        </div>

        {/* --- MAIN CONTENT LAYER --- */}
        {/* z-10 ensures it sits above the background/bars but below the Header (z-50) */}
        <div className='relative z-10 w-full max-w-6xl px-4 sm:px-6 flex flex-col items-center text-center gap-8 sm:gap-10 mt-6 min-[1070px]:mt-0'>
            
            {/* HEADLINE SECTION */}
            <section className='flex flex-col items-center'>
                <div className="font-montserrat font-bold text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-tight text-white tracking-tight">
                    <div className="overflow-hidden mb-2 sm:mb-4">
                        <span ref={line1Ref} className="inline-block mr-3 sm:mr-5">Build Your</span>
                        {/* Gold Gradient Text Class */}
                        <span ref={line1EmphRef} className="empire-text inline-block">
                            Legacy
                        </span>
                    </div>
                    <div className="overflow-hidden">
                        <span ref={line2Ref} className="inline-block mr-3 sm:mr-5">Lead with</span>
                        <span ref={line2EmphRef} className="empire-text inline-block">
                            Confidence
                        </span>
                    </div>
                </div>
                
                <p ref={paraRef} className="mt-6 text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
                    Strategies that <span className="text-secondary font-medium">Educate</span>, <span className="text-secondary font-medium">Elevate</span>, and <span className="text-secondary font-medium">Empower</span> — giving you Clarity, Confidence, and Control.
                </p>
            </section>

            {/* CTA SECTION */}
            <section className='flex flex-col items-center gap-8 w-full'>
                
                {/* Divider with Subtext */}
                <div ref={dividerRef} className='flex flex-col items-center gap-3'>
                    <div className='w-24 h-[1px] bg-gradient-to-r from-transparent via-secondary to-transparent'></div>
                    <h4 className='text-lg sm:text-xl font-serif italic text-gray-400'>
                        Your legacy begins here
                    </h4>
                </div>

                {/* Main CTA Button with Shimmer */}
                <div ref={ctaRef} className='group'>
                    <a href="#contact" className="block relative">
                        {/* Glow effect behind button */}
                        <span className="absolute -inset-1 bg-gradient-to-r from-secondary via-yellow-400 to-secondary rounded-full opacity-0 group-hover:opacity-50 blur-md transition duration-500"></span>
                        
                        <Button 
                            variant='bg-custom' 
                            className="relative flex items-center px-12 py-5 text-lg font-bold rounded-full gap-3 text-black bg-gradient-to-r from-secondary via-[#F4E080] to-secondary shadow-2xl overflow-hidden hover:scale-105 transition-transform duration-300"
                        >
                            {/* The Shimmer Animation */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent skew-x-[-12deg] w-1/3 animate-shimmer"></div>
                            
                            <span className="relative z-10 flex items-center gap-2">
                                Build Your Empire
                                <ArrowRightIcon className="w-5 h-5" />
                            </span>
                        </Button>
                    </a>
                </div>
            </section>
        </div>

        {/* --- SCROLL INDICATOR --- */}
        <div ref={scrollIndicatorRef} className="hidden min-[1070px]:block absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
            <a 
                href="#contact" 
                className='flex flex-col items-center gap-2 text-gray-400 hover:text-secondary transition-colors duration-300 cursor-pointer animate-bounce'
                aria-label="Scroll Down"
            >
                <span className="text-xs uppercase tracking-widest hidden sm:block">Scroll</span>
                <ArrowDownIcon className='w-6 h-6 p-1 rounded-full border border-gray-600 hover:border-secondary transition-colors'/>
            </a>
        </div>
    </div>
  )
}

export default Hero